import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Box, Button, Collapse, DialogContent, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { EmailOutlined, ExpandLess, ExpandMore, PeopleOutlineOutlined, PhoneOutlined, PlaceOutlined, ScheduleOutlined } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Address from '../address/Address';
import useResolvedAdditionalAddress from '../address/hook/useResolvedAdditionalAddress';
import CompanyContactList from '../../CompanyContactList';
import DeliveryHours from '../deliveryHours/DeliveryHours';
import { ellipsisStyle } from '../../../common/utils/style.utils';
import CopyToClipboard from '../../../common/components/CopyToClipboard';
import { BuyerInfoViewProps } from './BuyerInfoDialog';

type CategoryName = 'address' | 'contact' | 'delivery_hours';

export default function BuyerInfoDeliveryView({ company, close, changeView, hideBackArrow }: BuyerInfoViewProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts']);
  const deliveryAddress = useResolvedAdditionalAddress({ companyName: company.tradeName, companyAddress: company.addresses[0], additionalAddress: company.delivery_address });
  const [currentExpandedAccordion, setCurrentExpandedAccordion] = useState<CategoryName | undefined>('address');

  const deliveryContacts = useMemo(() => {
    return company.role_assignments.filter(({ is_delivery_contact }) => is_delivery_contact).map(({ user }) => user);
  }, [company]);

  const toggleAccordion = (name: CategoryName): void => {
    setCurrentExpandedAccordion(currentExpandedAccordion === name ? undefined : name);
  };

  return (
    <DialogContent sx={{ maxWidth: 500, height: 620, p: 2, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="text"
            onClick={() => changeView('GENERAL')}
            sx={{
              display: hideBackArrow ? 'none' : undefined,
            }}
          >
            <ArrowBackIcon />
          </Button>
          <LocalShippingOutlinedIcon sx={{ mx: 1 }} />
          <Typography variant="titleMedium" sx={{ flex: 1 }}>
            {t('common:delivery')}
          </Typography>
          <Button variant="text" onClick={close}>
            <CloseIcon />
          </Button>
        </Box>
        <Typography variant="bodySmall" color="onSurfaceVariant">
          {t('contracts:buyer_delivery_information')}
        </Typography>
      </Box>
      <Box
        sx={{
          overflow: 'auto',
        }}
      >
        <List>
          <ListItemButton onClick={() => toggleAccordion('address')}>
            <ListItemIcon>
              <PlaceOutlined />
            </ListItemIcon>
            <ListItemText primary={t('common:address')} />
            <ListItemIcon>
              {currentExpandedAccordion === 'address' ? <ExpandLess /> : <ExpandMore />}
            </ListItemIcon>
          </ListItemButton>
          <Collapse in={currentExpandedAccordion === 'address'} timeout="auto" unmountOnExit sx={{ p: 2 }}>
            <Address
              address={deliveryAddress}
              additionalInfoTitle={t('settings:delivery_instructions')}
              additionalInfo={company.delivery_instructions}
              email={company.delivery_email}
              phoneNumber={company.delivery_phone_number}
              hidePhone
              enableMapLink
            />
          </Collapse>
          <Divider />

          <ListItemButton onClick={() => toggleAccordion('contact')}>
            <ListItemIcon>
              <PeopleOutlineOutlined />
            </ListItemIcon>
            <ListItemText primary={t('common:contacts')} />
            <ListItemIcon>
              {currentExpandedAccordion === 'contact' ? <ExpandLess /> : <ExpandMore />}
            </ListItemIcon>
          </ListItemButton>
          <Collapse in={currentExpandedAccordion === 'contact'} timeout="auto" unmountOnExit sx={{ p: 2 }}>
            {deliveryContacts.length
              ? (
                <CompanyContactList contacts={deliveryContacts} displayContactInfo />
              ) : (
                <Typography variant="bodySmall" color="onSurfaceVariant" sx={{ px: 2, pt: 1, pb: 2 }}>
                  {t('contracts:no_delivery_contact_registered')}
                </Typography>
              )}
          </Collapse>
          <Divider />

          <ListItemButton onClick={() => toggleAccordion('delivery_hours')}>
            <ListItemIcon>
              <ScheduleOutlined />
            </ListItemIcon>
            <ListItemText primary={t('contracts:reception_hours')} />
            <ListItemIcon>
              {currentExpandedAccordion === 'delivery_hours' ? <ExpandLess /> : <ExpandMore />}
            </ListItemIcon>
          </ListItemButton>
          <Collapse in={currentExpandedAccordion === 'delivery_hours'} timeout="auto" unmountOnExit sx={{ p: 2 }}>
            {company.delivery_hours?.length
              ? (
                <DeliveryHours deliveryHours={company.delivery_hours} deliveryDescription={company.delivery_description} />
              ) : (
                <Typography variant="bodySmall" color="onSurfaceVariant">
                  {t('settings:no_delivery_hours')}
                </Typography>
              )}
          </Collapse>
          <Divider />

          <ListItem>
            <ListItemIcon>
              <PhoneOutlined />
            </ListItemIcon>
            <ListItemText>
              {company.delivery_phone_number || '-'}
            </ListItemText>
          </ListItem>
          <Divider />

          <ListItem>
            <ListItemIcon>
              <EmailOutlined />
            </ListItemIcon>
            <ListItemText>
              {company.delivery_email ? (
                <CopyToClipboard textToCopy={company.delivery_email}>
                  <Typography
                    variant="bodyMedium"
                    sx={{
                      ...ellipsisStyle,
                    }}
                  >
                    {company.delivery_email}
                  </Typography>
                </CopyToClipboard>
              ) : '-'}
            </ListItemText>
          </ListItem>
        </List>
      </Box>
    </DialogContent>
  );
}
